import * as React from "react";
import styled from "styled-components";
import Pile from "./Pile";
import Card from "./Card";
import DroppablePile from "./DroppablePile";
import { useEffect } from "react";
import GameRule from "./Types/GameRule";
import PileType from "./Types/Pile";
import Direction from "./Types/Direction";
import Player from "./Types/Player";
import { LEGACY_convertToPiles, getIsSpeakingAllowed } from "./utils";
import PlayedCard from "./Types/PlayedCard";

interface Props {
  selectedCard?: any;
  onCardPlayed: any;
  cardsInDeck?: number;
  playedCards: PlayedCard[];
  gameRule: GameRule;
  interestedPlayers: { pileIndex: number; players: Player[] }[];
  onShowInterest: (pileIndex: number) => void;
  availablePilesPerCard: {
    [c: number]: PileType[];
  };
  gameId: number;
}

const StyledPileContainer = styled.div`
  display: grid;
  grid-gap: 0.3em;
  grid-auto-flow: column;
  font-size: 5rem;
  margin-top: 5rem;
`;

const StyledPileWrapper = styled.div<{ index: number }>`
  margin-top: ${({ index }) => `-${index * 0.007}em`};
  position: absolute;
  z-index: 1;
`;

const StyledDeck = styled.div<{ cardsInDeck?: number }>`
  position: relative;
  width: 3em;
  height: 2em;
`;

const StyledCard = styled(Card)<{ index: number }>`
  position: absolute;
  box-shadow: none;
  top: ${({ index }) => `-${index * 0.005}em`};

  > div {
    border-bottom: 1px solid #26242e;
  }

  border-radius: 0.15em;
`;

const PileContainer = ({
  selectedCard,
  onCardPlayed,
  cardsInDeck,
  playedCards,
  gameRule,
  interestedPlayers,
  onShowInterest,
  availablePilesPerCard,
  gameId,
}: Props) => {
  const [isSpeakingAllowed, setIsSpeakingAllowed] = React.useState(true);

  const deckCards: React.ReactNode[] = cardsInDeck
    ? Array.from({ length: cardsInDeck }, (v, k) => k + 1).map((_, i) => (
        <StyledCard
          key={i}
          isFacingDown={true}
          index={i}
          gameId={gameId}
        ></StyledCard>
      ))
    : [];

  useEffect(() => {
    setIsSpeakingAllowed(
      getIsSpeakingAllowed(gameRule, gameId, LEGACY_convertToPiles(playedCards))
    );
  }, [gameId, gameRule, playedCards]);

  return (
    <StyledPileContainer>
      {[1, 2].map((i) => {
        let cardsInThisPile = playedCards.filter(
          (playedCard) => playedCard.pileIndex === i
        );

        let cardsInPile = Array.from(
          { length: cardsInThisPile.length },
          (v, k) => k + 1
        ).map((_, i) => (
          <StyledCard
            key={i}
            isFacingDown={false}
            index={i}
            gameId={gameId}
          ></StyledCard>
        ));

        return (
          <StyledDeck cardsInDeck={cardsInThisPile.length} key={i}>
            <DroppablePile
              key={i}
              enabled={
                !!availablePilesPerCard[selectedCard]?.find(
                  (p) => p.index === i
                )
              }
              onDrop={() => onCardPlayed(i)}
            >
              <div style={{ position: "relative" }}>{cardsInPile}</div>
              <StyledPileWrapper key={i} index={cardsInThisPile.length}>
                <Pile
                  isSpeakingAllowed={isSpeakingAllowed}
                  index={i}
                  selectedCard={selectedCard}
                  direction={Direction.Up}
                  numberOfCards={cardsInThisPile.length}
                  number={
                    cardsInThisPile[cardsInThisPile.length - 1]?.playedCard
                  }
                  gameRule={gameRule}
                  enabled={
                    !!availablePilesPerCard[selectedCard]?.find(
                      (p) => p.index === i
                    )
                  }
                  interestedPlayers={
                    interestedPlayers.find((x) => x.pileIndex === i)?.players ??
                    []
                  }
                  onShowInterest={onShowInterest}
                  gameId={gameId}
                />
              </StyledPileWrapper>
            </DroppablePile>
          </StyledDeck>
        );
      })}
      <DroppablePile showBorder={false} flex={true} enabled={false}>
        <StyledDeck cardsInDeck={cardsInDeck}>{deckCards}</StyledDeck>
      </DroppablePile>
      {[3, 4].map((i) => {
        let cardsInThisPile = playedCards.filter(
          (playedCard) => playedCard.pileIndex === i
        );

        let cardsInPile = Array.from(
          { length: cardsInThisPile.length },
          (v, k) => k + 1
        ).map((_, i) => (
          <StyledCard
            key={i}
            isFacingDown={false}
            index={i}
            gameId={gameId}
          ></StyledCard>
        ));

        return (
          <StyledDeck cardsInDeck={cardsInThisPile.length} key={i}>
            <DroppablePile
              key={i}
              enabled={
                !!availablePilesPerCard[selectedCard]?.find(
                  (p) => p.index === i
                )
              }
              onDrop={() => onCardPlayed(i)}
            >
              <div style={{ position: "relative" }}>{cardsInPile}</div>
              <StyledPileWrapper key={i} index={cardsInThisPile.length}>
                <Pile
                  isSpeakingAllowed={isSpeakingAllowed}
                  index={i}
                  selectedCard={selectedCard}
                  direction={Direction.Down}
                  numberOfCards={cardsInThisPile.length}
                  number={
                    cardsInThisPile[cardsInThisPile.length - 1]?.playedCard
                  }
                  enabled={
                    !!availablePilesPerCard[selectedCard]?.find(
                      (p) => p.index === i
                    )
                  }
                  gameRule={gameRule}
                  interestedPlayers={
                    interestedPlayers.find((x) => x.pileIndex === i)?.players ??
                    []
                  }
                  onShowInterest={onShowInterest}
                  gameId={gameId}
                />
              </StyledPileWrapper>
            </DroppablePile>
          </StyledDeck>
        );
      })}
    </StyledPileContainer>
  );
};
export default PileContainer;
